import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { formatChf } from '@/lib/format';
import { toast } from 'sonner';

export const Route = createFileRoute('/goals')({
  component: GoalsPage,
});

function GoalsPage() {
  const qc = useQueryClient();
  const { data: goals } = useQuery({ queryKey: ['goals'], queryFn: api.listGoals });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', targetAmount: '', currentAmount: '0', deadline: '' });
  const [coachContent, setCoachContent] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api.createGoal({
        name: form.name,
        targetAmount: Number(form.targetAmount),
        currentAmount: Number(form.currentAmount),
        deadline: form.deadline || undefined,
        status: 'active',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['goals'] });
      toast.success('Objectif créé');
      setOpen(false);
      setForm({ name: '', targetAmount: '', currentAmount: '0', deadline: '' });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteGoal(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['goals'] });
      toast.success('Objectif supprimé');
    },
  });

  const reviewMut = useMutation({
    mutationFn: (id: string) => api.reviewGoal(id),
    onSuccess: (r) => {
      setCoachContent(r.content);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Objectifs</h1>
          <p className="text-sm text-muted-foreground">Vacances, achat, fonds d'urgence — surveille ta progression</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" /> Nouvel objectif
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvel objectif d'épargne</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Nom</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Vacances 2026"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Montant cible (CHF)</Label>
                  <Input
                    type="number"
                    value={form.targetAmount}
                    onChange={(e) => setForm((f) => ({ ...f, targetAmount: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Déjà épargné</Label>
                  <Input
                    type="number"
                    value={form.currentAmount}
                    onChange={(e) => setForm((f) => ({ ...f, currentAmount: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Deadline (optionnelle)</Label>
                <Input
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={() => createMut.mutate()} disabled={!form.name || !form.targetAmount}>
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {(goals ?? []).map((g) => (
          <Card key={g.goal.id}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>{g.goal.name}</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {formatChf(g.goal.currentAmount)} / {formatChf(g.goal.targetAmount)}
                  {g.goal.deadline ? <> · échéance {g.goal.deadline}</> : null}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (window.confirm('Supprimer cet objectif ?')) deleteMut.mutate(g.goal.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <Progress value={g.pct * 100} />
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={g.onTrack === false ? 'warning' : g.onTrack ? 'success' : 'secondary'}>
                  {Math.round(g.pct * 100)} %
                </Badge>
                {g.monthlyVelocity > 0 ? (
                  <span className="text-muted-foreground">≈ {formatChf(g.monthlyVelocity)}/mois</span>
                ) : (
                  <span className="text-muted-foreground">Pas de vélocité connue</span>
                )}
                {g.projectedReachDate ? (
                  <span className="text-muted-foreground">Projection : {g.projectedReachDate}</span>
                ) : null}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => reviewMut.mutate(g.goal.id)}
                disabled={reviewMut.isPending}
              >
                <Sparkles className="h-4 w-4" />
                {reviewMut.isPending ? 'Analyse…' : 'Coach IA'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {coachContent ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Coach
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{coachContent}</ReactMarkdown>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
